// /win — Hidden Home Show QR-code landing page.
// Standalone: no shared homepage/products/CTA/calculator/CRM/email code is
// touched. Submits to isolated backend endpoint POST /api/win/entries.
// Sets <meta name="robots" content="noindex, nofollow"> so it stays out of
// Google. Meta is removed on unmount to avoid leaking to other routes.
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/forms/AddressAutocomplete";
import {
  Facebook,
  Instagram,
  CheckCircle2,
  Gift,
  QrCode,
  ShieldCheck,
  Sparkles,
  ArrowRight,
} from "lucide-react";

const NEOPOWER_URL =
  "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/8f83fuhq_neopower-product.png";
const FB_URL = "https://www.facebook.com/profile.php?id=61590325272812";
const IG_URL =
  "https://www.instagram.com/aeromax_energy?igsh=OW5lNDFvamxqdmdt";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const HOMEOWNER_OPTS = ["Yes", "No", "Planning / building soon"];
const SYSTEM_OPTS = [
  "Electric cylinder",
  "Gas califont",
  "LPG bottles",
  "Gas storage",
  "Not sure",
  "Other",
];
const ESTIMATE_OPTS = ["Yes", "No", "Maybe later"];
const FOLLOWED_OPTS = ["Facebook", "Instagram", "Both", "I will do it now"];

// -------------------- meta tag manager (noindex) --------------------
function useNoIndex() {
  useEffect(() => {
    // Add robots noindex meta while this page is mounted.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    meta.setAttribute("data-win-noindex", "1");
    document.head.appendChild(meta);

    const prevTitle = document.title;
    document.title = "AeroMax Home Show Giveaway";

    return () => {
      const existing = document.head.querySelector(
        'meta[data-win-noindex="1"]'
      );
      if (existing) existing.remove();
      document.title = prevTitle;
    };
  }, []);
}

// ---------------- form ----------------
function EntryForm() {
  const [form, setForm] = useState({
    full_name: "",
    mobile: "",
    email: "",
    installation_address: "",
    homeowner: "",
    current_system: "",
    interested_estimate: "",
    followed: "",
    marketing_consent: false,
    terms_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.full_name.trim() ||
      !form.mobile.trim() ||
      !form.email.trim() ||
      !form.installation_address.trim()
    ) {
      return toast.error("Please fill in the required fields.");
    }
    if (!form.terms_consent) {
      return toast.error("Please accept the terms to enter the draw.");
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/win/entries`, form, {
        headers: { "Content-Type": "application/json" },
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="win-success"
        className="rounded-3xl border border-[#10B981]/30 bg-[#0F1F45]/60 backdrop-blur p-8 sm:p-10 text-center"
      >
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#10B981]/15 text-[#10B981] mb-5">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight">
          Thank you for entering.
        </h3>
        <p className="mt-4 text-white/80 leading-relaxed max-w-lg mx-auto">
          Your AeroMax Energy Home Show Giveaway entry has been received. Please make sure you have followed AeroMax Energy on Facebook or Instagram to complete your entry.
        </p>
        <p className="mt-3 text-xs text-white/60 leading-relaxed max-w-lg mx-auto">
          The draw is subject to valid entries and promotion conditions being met.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <a
            href={FB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white px-5 py-2.5 text-sm font-semibold transition-all"
          >
            <Facebook className="h-4 w-4" /> Open Facebook
          </a>
          <a
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white px-5 py-2.5 text-sm font-semibold transition-all"
          >
            <Instagram className="h-4 w-4" /> Open Instagram
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="win-entry-form"
      className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-lg p-6 sm:p-8 space-y-5"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <FieldWrapper label="Full Name *">
          <TextInput
            testId="win-name"
            value={form.full_name}
            onChange={(v) => set("full_name", v)}
            required
          />
        </FieldWrapper>
        <FieldWrapper label="Mobile Number *">
          <TextInput
            testId="win-mobile"
            type="tel"
            value={form.mobile}
            onChange={(v) => set("mobile", v)}
            required
          />
        </FieldWrapper>
        <FieldWrapper label="Email Address *">
          <TextInput
            testId="win-email"
            type="email"
            value={form.email}
            onChange={(v) => set("email", v)}
            required
          />
        </FieldWrapper>
        <FieldWrapper label="Address *">
          <AddressAutocomplete
            data-testid="win-address"
            value={form.installation_address}
            onChange={(v) => set("installation_address", v)}
            onPlaceSelected={(p) =>
              set(
                "installation_address",
                p.formatted_address || form.installation_address
              )
            }
            placeholder="Start typing your NZ address…"
            autoComplete="off"
            className="w-full rounded-lg border border-white/15 bg-white/10 h-11 px-3 text-sm text-white placeholder-white/50 focus:outline-none focus:border-[#10B981] focus-visible:ring-2 focus-visible:ring-[#10B981]/25 transition"
          />        </FieldWrapper>
      </div>

      <FieldWrapper label="Are you a homeowner?">
        <SelectInput
          testId="win-homeowner"
          value={form.homeowner}
          onChange={(v) => set("homeowner", v)}
          options={HOMEOWNER_OPTS}
        />
      </FieldWrapper>

      <FieldWrapper label="What hot water system do you currently have?">
        <SelectInput
          testId="win-system"
          value={form.current_system}
          onChange={(v) => set("current_system", v)}
          options={SYSTEM_OPTS}
        />
      </FieldWrapper>

      <FieldWrapper label="Would you like a free hot water savings estimate?">
        <SelectInput
          testId="win-estimate"
          value={form.interested_estimate}
          onChange={(v) => set("interested_estimate", v)}
          options={ESTIMATE_OPTS}
        />
      </FieldWrapper>

      <FieldWrapper label="Have you followed AeroMax Energy on Facebook or Instagram?">
        <SelectInput
          testId="win-followed"
          value={form.followed}
          onChange={(v) => set("followed", v)}
          options={FOLLOWED_OPTS}
        />
      </FieldWrapper>

      <div className="space-y-3 pt-2 border-t border-white/10">
        <ConsentRow
          testId="win-marketing"
          checked={form.marketing_consent}
          onChange={(v) => set("marketing_consent", v)}
          label="I agree to receive updates, offers, and marketing communications from AeroMax Energy. I understand I can unsubscribe at any time."
        />
        <ConsentRow
          testId="win-terms"
          checked={form.terms_consent}
          onChange={(v) => set("terms_consent", v)}
          label="I confirm I have read and agree to the AeroMax Energy Home Show Giveaway terms and conditions and privacy notice."
        />
      </div>

      <button
        type="submit"
        data-testid="win-submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white py-4 font-semibold text-base transition-all disabled:opacity-60 hover:shadow-lg hover:shadow-emerald-500/30"
      >
        {submitting ? "Sending…" : "Enter the Draw"}
        {!submitting && <ArrowRight className="h-4 w-4" />}
      </button>

      <p className="text-[11px] text-white/55 leading-relaxed pt-1">
        T&Cs apply. Prize is supply-only. Installation and site-specific works are not included unless confirmed in writing. Draw subject to valid entries and promotion conditions being met. This promotion is not sponsored, endorsed, administered by, or associated with Facebook or Instagram.
      </p>
    </form>
  );
}

// ---- tiny local field primitives (kept local — no shared component edits) ----
function FieldWrapper({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-white/80 tracking-wide uppercase">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function TextInput({ testId, value, onChange, type = "text", required = false }) {
  return (
    <input
      data-testid={testId}
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-white/15 bg-white/10 px-3 h-11 text-sm text-white placeholder-white/50 focus:outline-none focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/25 transition"
      autoComplete="off"
    />
  );
}

function SelectInput({ testId, value, onChange, options }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-white/15 bg-white/10 px-3 h-11 text-sm text-white focus:outline-none focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/25 transition"
    >
      <option value="" className="bg-[#0B132B]">Select…</option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0B132B]">
          {o}
        </option>
      ))}
    </select>
  );
}

function ConsentRow({ testId, checked, onChange, label }) {
  return (
    <label className="flex items-start gap-3 text-sm text-white/80 leading-relaxed cursor-pointer">
      <input
        data-testid={testId}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-white/40 bg-white/10 text-[#10B981] focus:ring-[#10B981]/40"
      />
      <span>{label}</span>
    </label>
  );
}

// ------------------------------- Page -------------------------------
export default function Win() {
  useNoIndex();

  return (
    <div
      data-testid="win-page"
      className="relative min-h-screen bg-[#0B132B] text-white overflow-hidden"
    >
      {/* Ambient glow + subtle grid, kept local to /win only */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[540px] w-[820px] rounded-full bg-[#10B981]/12 blur-[130px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 h-[380px] w-[420px] rounded-full bg-[#10B981]/8 blur-[100px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-16">
        {/* Hero */}
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-start">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-[0.24em] uppercase font-bold text-white/90">
              <QrCode className="h-3 w-3 text-[#10B981]" /> Home Show Giveaway
            </span>
            <h1 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Follow <span className="text-[#10B981]">AeroMax Energy</span> and enter to win a FREE NeoPower Hot Water Heat Pump
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl">
              Visit AeroMax Energy at the Home Show. Follow us on Facebook or Instagram, complete the short entry form, and get a chance to win a NeoPower hot water heat pump unit — <span className="font-semibold text-white">supply only</span>.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={FB_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="win-cta-fb"
                className="inline-flex items-center gap-2 rounded-full bg-[#1877F2] hover:bg-[#0f65d3] text-white px-5 py-3 text-sm sm:text-[15px] font-semibold transition-all hover:shadow-lg"
              >
                <Facebook className="h-4 w-4" /> Follow on Facebook
              </a>
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="win-cta-ig"
                className="inline-flex items-center gap-2 rounded-full text-white px-5 py-3 text-sm sm:text-[15px] font-semibold transition-all hover:shadow-lg"
                style={{
                  background:
                    "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                }}
              >
                <Instagram className="h-4 w-4" /> Follow on Instagram
              </a>
              <a
                href="#entry-form"
                data-testid="win-cta-entry"
                className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-5 py-3 text-sm sm:text-[15px] font-semibold transition-all hover:shadow-lg hover:shadow-emerald-500/30"
              >
                <Gift className="h-4 w-4" /> Complete Entry Form
              </a>
            </div>
          </div>

          {/* Prize card */}
          <aside className="relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#050914]">
              <img
                src={NEOPOWER_URL}
                alt="NeoPower Hot Water Heat Pump"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981] text-white text-[10px] font-bold tracking-[0.16em] uppercase">
                <Sparkles className="h-3 w-3" /> Supply-only prize
              </span>
            </div>
            <div className="mt-5">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">
                The prize
              </p>
              <h3 className="font-heading mt-2 text-xl sm:text-2xl font-bold text-white leading-snug">
                Win one NeoPower Hot Water Heat Pump unit
              </h3>
              <p className="mt-3 text-[13px] text-white/70 leading-relaxed">
                The prize is the unit only. Installation, plumbing, electrical work, delivery, travel, removal of existing systems, and site-specific works are not included unless confirmed in writing by AeroMax Energy.
              </p>
            </div>
          </aside>
        </div>

        {/* How it works */}
        <section className="mt-16 sm:mt-24">
          <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#10B981]">
            How it works
          </p>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight max-w-2xl">
            Three quick steps to enter.
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                n: "1",
                icon: Facebook,
                title: "Follow us",
                body:
                  "Follow AeroMax Energy on Facebook or Instagram to stay in the loop.",
              },
              {
                n: "2",
                icon: Gift,
                title: "Complete the form",
                body:
                  "Fill in the short entry form on this page — takes under a minute.",
              },
              {
                n: "3",
                icon: ShieldCheck,
                title: "Go into the draw",
                body:
                  "Subject to valid entries and promotion conditions being met.",
              },
            ].map((s, i) => (
              <div
                key={i}
                className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#10B981]/15 text-[#10B981] font-heading font-bold">
                    {s.n}
                  </span>
                  <s.icon className="h-4 w-4 text-white/60" />
                </div>
                <h3 className="font-heading mt-4 text-lg sm:text-xl font-bold text-white leading-snug">
                  {s.title}
                </h3>
                <p className="mt-1.5 text-sm text-white/70 leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Entry form */}
        <section id="entry-form" className="mt-16 sm:mt-24 scroll-mt-16">
          <div className="max-w-2xl">
            <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#10B981]">
              Your entry
            </p>
            <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
              Complete your entry.
            </h2>
            <p className="mt-3 text-white/70 leading-relaxed">
              We&apos;ll be in touch after the promotion closes if you&apos;re the lucky winner.
            </p>
          </div>
          <div className="mt-7 max-w-2xl">
            <EntryForm />
          </div>
        </section>
      </div>
    </div>
  );
}
